import {create, type StateCreator} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

import {createDebouncedSessionStorage} from '../composer/storage';
import type {ContentPart} from '../store';

export interface QueuedMessage {
  content: ContentPart[];
  /**
   * draining marks the head item mid-send, from the drainer's pop-and-initiate
   * until `turn-created` confirms it reached the server.
   */
  draining?: boolean;
  /** enqueuedAt is the enqueue timestamp. */
  enqueuedAt: number;
  /** id is a stable client id for remove/reorder/edit. */
  id: string;
  runConfig: {
    custom: {agentConfigOverride?: string | null; permissions?: unknown};
  };
}

export interface QueueState {
  queues: Record<string, QueuedMessage[]>;
  resumeRequested: Record<string, boolean>;

  /** clear removes key's whole queue. */
  clear: (key: string) => void;
  /**
   * clearDraining clears the draining flag after a terminal event with no drain
   * confirm; the item stays queued.
   */
  clearDraining: (key: string) => void;
  /**
   * confirmDrained removes the draining head once turn-created confirms the
   * send.
   */
  confirmDrained: (key: string) => void;
  /** consumeResume clears the resume flag for key. */
  consumeResume: (key: string) => void;
  enqueue: (key: string, msg: QueuedMessage) => void;
  /**
   * markDraining flags id as draining. `clearDraining`/`confirmDrained`
   * operate on whichever item under key is draining, not by id, so callers
   * must guarantee at most one draining item per key at a time.
   */
  markDraining: (key: string, id: string) => void;
  /**
   * promote moves a queue from oldKey to the canonical thread id's key; the
   * target key must not preexist.
   */
  promote: (oldKey: string, threadId: string) => void;
  remove: (key: string, id: string) => void;
  /**
   * requestResume sets the resume flag for key and marks its current items
   * eligible.
   */
  requestResume: (key: string) => void;
}

type QueueDataSlice = Pick<
  QueueState,
  | 'clear'
  | 'clearDraining'
  | 'confirmDrained'
  | 'enqueue'
  | 'markDraining'
  | 'promote'
  | 'queues'
  | 'remove'
>;

type ResumeSlice = Pick<
  QueueState,
  'consumeResume' | 'requestResume' | 'resumeRequested'
>;

/**
 * queueKey maps a thread id (optimistic client id before promotion, or the
 * server-assigned thread id after) to its queue key.
 *
 * NOTE: Queueing is not supported for the "new chat" view, since queueing
 * always requires a thread to be running (implying a message has already been
 * sent).
 */
export function queueKey(threadId: string): string {
  return `thread:${threadId}`;
}

export function createQueueStore() {
  // NOTE: Unpersisted. Items are armed via `enqueue`, `requestResume`, or a
  // rehydration `merge`; the merge re-arm lets an in-session reload resume
  // auto-sending its restored queue.
  const eligibleIds = new Set<string>();

  // NOTE: Unpersisted per-key watermark of the last clear timestamp. A cleared
  // queue is not persisted, so a reload has nothing stale to revive.
  const lastClearedAt = new Map<string, number>();

  const store = create<QueueState>()(
    devtools(
      persist(
        (...args) => ({
          ...createQueueDataSlice(eligibleIds, lastClearedAt)(...args),
          ...createResumeSlice(eligibleIds)(...args),
        }),
        {
          name: 'wb-agent-message-queue',
          version: 1,
          storage: createJSONStorage(createDebouncedSessionStorage),
          // NOTE: A message carrying an image is dropped wholesale (not
          // demoted to a text-only remnant), since auto-sending it with the
          // attachment silently removed would be a correctness bug.
          partialize: state => ({
            queues: Object.fromEntries(
              Object.entries(state.queues)
                .map(
                  ([key, items]) =>
                    [
                      key,
                      items
                        .filter(m => !hasImagePart(m.content))
                        .map(
                          ({draining: _draining, ...persisted}) => persisted
                        ),
                    ] as const
                )
                .filter(([, items]) => items.length > 0)
            ),
          }),
          merge: (persisted, current) => {
            const queues =
              (persisted as {queues?: Record<string, QueuedMessage[]>})
                ?.queues ?? {};
            // Re-arm restored items as eligible. A persisted queue only
            // survives an in-session reload (sessionStorage), so resuming its
            // auto-send matches intent rather than reviving stale work.
            for (const items of Object.values(queues)) {
              for (const item of items) {
                eligibleIds.add(item.id);
              }
            }
            return {...current, queues};
          },
        }
      ),
      {name: 'QueueStore'}
    )
  );

  return Object.assign(store, {
    isEligible: (id: string) => eligibleIds.has(id),
  });
}

export const useQueueStore = createQueueStore();

function createQueueDataSlice(
  eligibleIds: Set<string>,
  lastClearedAt: Map<string, number>
): StateCreator<QueueState, [], [], QueueDataSlice> {
  return (set, get) => ({
    queues: {},

    clear: key => {
      for (const msg of get().queues[key] ?? []) {
        eligibleIds.delete(msg.id);
      }

      lastClearedAt.set(key, Date.now());

      set(s => {
        const {[key]: _removed, ...queues} = s.queues;
        const {[key]: _removedResume, ...resumeRequested} = s.resumeRequested;
        return {queues, resumeRequested};
      });
    },

    clearDraining: key =>
      set(s => ({
        queues: updateQueue(s.queues, key, q =>
          q.map(m => (m.draining ? {...m, draining: false} : m))
        ),
      })),

    confirmDrained: key =>
      set(s => ({
        queues: pruneIfEmpty(
          updateQueue(s.queues, key, q => q.filter(m => !m.draining)),
          key
        ),
      })),

    enqueue: (key, msg) => {
      const clearedAt = lastClearedAt.get(key);
      if (clearedAt != null && msg.enqueuedAt <= clearedAt) {
        return;
      }

      eligibleIds.add(msg.id);
      set(s => ({queues: updateQueue(s.queues, key, q => [...q, msg])}));
    },

    markDraining: (key, id) =>
      set(s => ({
        queues: updateQueue(s.queues, key, q =>
          q.map(m => (m.id === id ? {...m, draining: true} : m))
        ),
      })),

    promote: (oldKey, threadId) =>
      set(s => {
        const items = s.queues[oldKey];
        if (items == null) {
          return s;
        }
        const newKey = queueKey(threadId);
        const {[oldKey]: _removed, ...rest} = s.queues;
        const {[oldKey]: _removedResume, ...restResume} = s.resumeRequested;
        return {
          queues: {...rest, [newKey]: items},
          resumeRequested: s.resumeRequested[oldKey]
            ? {...restResume, [newKey]: true}
            : restResume,
        };
      }),

    remove: (key, id) => {
      eligibleIds.delete(id);
      set(s => ({
        queues: pruneIfEmpty(
          updateQueue(s.queues, key, q => q.filter(m => m.id !== id)),
          key
        ),
      }));
    },
  });
}

function createResumeSlice(
  eligibleIds: Set<string>
): StateCreator<QueueState, [], [], ResumeSlice> {
  return (set, get) => ({
    resumeRequested: {},

    consumeResume: key =>
      set(s => {
        const {[key]: _removed, ...resumeRequested} = s.resumeRequested;
        return {resumeRequested};
      }),

    requestResume: key => {
      for (const msg of get().queues[key] ?? []) {
        eligibleIds.add(msg.id);
      }
      set(s => ({
        resumeRequested: {...s.resumeRequested, [key]: true},
      }));
    },
  });
}

export function hasImagePart(content: ContentPart[]): boolean {
  return content.some(p => p.type === 'image');
}

function pruneIfEmpty(
  queues: Record<string, QueuedMessage[]>,
  key: string
): Record<string, QueuedMessage[]> {
  if ((queues[key]?.length ?? 0) > 0) {
    return queues;
  }

  const {[key]: _removed, ...rest} = queues;
  return rest;
}

/**
 * updateQueue returns a new queues map with `update` applied to the entry at
 * `key`, seeding an empty queue when the key is absent.
 */
function updateQueue(
  queues: Record<string, QueuedMessage[]>,
  key: string,
  update: (items: QueuedMessage[]) => QueuedMessage[]
): Record<string, QueuedMessage[]> {
  return {...queues, [key]: update(queues[key] ?? [])};
}
